import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MaterialBadge } from "@/components/MaterialBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, Plus, MapPin, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CLASES = {
  muy_recomendable: { label: "Muy recomendable", desc: "prácticamente en el camino", cls: "bg-emerald-500/15 border-emerald-500/50 text-emerald-200" },
  recomendable: { label: "Recomendable", desc: "pequeño desvío", cls: "bg-cyan-500/15 border-cyan-500/50 text-cyan-200" },
  posible: { label: "Posible", desc: "desvío mayor pero asumible", cls: "bg-amber-500/15 border-amber-500/50 text-amber-200" },
};
const SITUACION = { atrasado: { t: "ATRASADO", cls: "bg-orange-500/20 text-orange-200 border-orange-500/40" }, sin_fecha: { t: "SIN FECHA", cls: "bg-zinc-700/60 text-zinc-200 border-zinc-600" } };

function DestinoItem({ d, onAnadir, disabled }) {
  const c = CLASES[d.clasificacion] || CLASES.posible;
  const fechas = [...new Set(d.lineas.map((l) => l.fecha_montaje).filter(Boolean))];
  return (
    <li className={`rounded-lg border p-2.5 ${c.cls} ${!d.cabe ? "opacity-70" : ""}`} data-testid={`oportunidad-${d.destino_key}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-current/40" data-testid={`clasificacion-${d.destino_key}`}>{c.label}</span>
            <span className="font-mono font-bold" data-testid={`desvio-${d.destino_key}`}>+{d.desvio_km} km{d.desvio_min != null ? ` · +${d.desvio_min} min` : ""}</span>
            <span className="text-[10px] opacity-60">{d.metrica === "ors_carretera" ? "carretera (ORS)" : "estimado"}</span>
            {d.n_lineas > 1 && <span className="text-[10px] px-1 rounded bg-zinc-900/60">×{d.n_lineas} trabajos</span>}
          </div>
          <div className="font-semibold mt-1 truncate text-zinc-100">{d.espacio_nombre || d.direccion || "—"} <span className="font-normal opacity-70">· {d.ciudad}{d.provincia ? ` (${d.provincia})` : ""}</span></div>
          <div className="text-[11px] opacity-80 truncate">{d.direccion}</div>
          <div className="flex flex-wrap gap-1 mt-1 text-[10px]">
            {d.campanas.map((x) => <span key={x} className="font-mono bg-zinc-950/50 px-1.5 py-0.5 rounded">{x}</span>)}
            {fechas.map((x) => <span key={x} className="px-1.5 py-0.5 rounded bg-zinc-950/50">📅 {x}</span>)}
            {d.situaciones.filter((s) => SITUACION[s]).map((s) => <span key={s} className={`px-1 py-0.5 rounded border ${SITUACION[s].cls}`}>{SITUACION[s].t}</span>)}
            {(d.lineas || []).some((l) => l.disponibilidad)
              ? (d.lineas || []).slice(0, 4).map((l) => <MaterialBadge key={l.linea_id} disponibilidad={l.disponibilidad} compact={d.n_lineas > 1} testId={`oportunidad-material-${l.linea_id}`} />)
              : <span className={`px-1.5 py-0.5 rounded ${d.material_apto_todas ? "bg-emerald-500/20 text-emerald-200" : "bg-rose-500/20 text-rose-200"}`}>{d.material_apto_todas ? "material apto" : "material pendiente"}</span>}
            {d.horario && <span className="px-1.5 py-0.5 rounded bg-zinc-950/50 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{d.horario}</span>}
          </div>
          {!d.cabe && <div className="text-[10px] text-rose-300 mt-1 flex items-center gap-1" data-testid={`no-cabe-${d.destino_key}`}><AlertTriangle className="w-3 h-3" /> No cabe: la ruta superaría el máximo de paradas ({d.n_lineas} trabajo(s), hueco insuficiente)</div>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {d.lat && <a href={`https://www.google.com/maps?q=${d.lat},${d.lon}`} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200"><MapPin className="w-3.5 h-3.5" /></a>}
          <Button size="sm" onClick={() => onAnadir(d)} disabled={disabled || !d.cabe} data-testid={`anadir-oportunidad-${d.destino_key}`}
            className="h-7 text-[11px] bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold disabled:opacity-40"><Plus className="w-3 h-3 mr-1" /> Añadir a esta ruta</Button>
        </div>
      </div>
    </li>
  );
}

/** Buscar oportunidades cercanas al corredor de una ruta del borrador. Nunca añade automáticamente. */
export default function OportunidadesDialog({ grupo, grupos, excluidas, filtrosPayload, inicio, open, onOpenChange, onAnadir }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [umbrales, setUmbrales] = useState({ muy_recomendable: 5, recomendable: 15, posible: 40 });
  const [respetarCampanas, setRespetarCampanas] = useState(false);
  const [usarOrs, setUsarOrs] = useState(true);

  const buscar = async () => {
    if (!grupo) return;
    setLoading(true);
    try {
      const { data: d } = await api.post("/reparto-inteligente/oportunidades", {
        filtros: { ...filtrosPayload, provincias: [], ciudades: [] },
        ruta: { id: grupo.id, paradas: grupo.paradas },
        excluir_linea_ids: [...grupos.flatMap((g) => g.paradas.map((p) => p.linea_id)), ...excluidas.map((p) => p.linea_id)],
        max_paradas: grupo.capacidad ?? null, inicio_lat: inicio?.lat, inicio_lon: inicio?.lon,
        umbrales: { muy_recomendable: Number(umbrales.muy_recomendable), recomendable: Number(umbrales.recomendable), posible: Number(umbrales.posible) },
        respetar_campanas: respetarCampanas, usar_ors: usarOrs,
      });
      setData(d);
      (d.avisos || []).forEach((a) => toast.warning(a, { duration: 7000 }));
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron buscar oportunidades"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && grupo) { setData(null); buscar(); } }, [open, grupo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const anadir = (d) => {
    const r = onAnadir(grupo.id, d.lineas);
    if (r?.error) { toast.error(r.error, { duration: 8000 }); return; }
    toast.success(`${d.n_lineas} trabajo(s) de «${d.espacio_nombre || d.ciudad}» añadido(s) a ${grupo.id} · ruta reordenada · puedes Deshacer`);
    setData((prev) => prev ? { ...prev, destinos: prev.destinos.filter((x) => x.destino_key !== d.destino_key), hueco: prev.hueco == null ? null : prev.hueco - d.n_lineas } : prev);
  };

  const hueco = grupo?.capacidad != null ? grupo.capacidad - grupo.paradas.length : null;
  const destinos = (data?.destinos || []).map((d) => ({ ...d, cabe: hueco == null || d.n_lineas <= hueco }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] flex flex-col" data-testid="oportunidades-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /> Oportunidades cercanas · {grupo?.id} {grupo?.ciudad_principal ? `· ${grupo.ciudad_principal}` : ""}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Trabajos pendientes elegibles (mismas reglas del planificador: fecha, material, no montados, no en ruta) que NO están en el borrador, ordenados por desvío sobre el corredor completo de la ruta. La provincia no es frontera. Nada se añade sin que lo confirmes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 text-[11px] bg-zinc-900/60 border border-zinc-800 rounded-lg p-2.5" data-testid="oportunidades-config">
          {["muy_recomendable", "recomendable", "posible"].map((k) => (
            <label key={k} className="flex flex-col gap-0.5 text-zinc-400">
              <span>≤ km · {CLASES[k].label}</span>
              <Input type="number" min="0" step="1" value={umbrales[k]} onChange={(e) => setUmbrales((u) => ({ ...u, [k]: e.target.value }))} data-testid={`umbral-${k}`} className="h-7 w-20 bg-zinc-950 border-zinc-800 text-xs" />
            </label>
          ))}
          <label className="inline-flex items-center gap-1.5 text-zinc-300 cursor-pointer pb-1.5">
            <input type="checkbox" checked={respetarCampanas} onChange={(e) => setRespetarCampanas(e.target.checked)} data-testid="respetar-campanas" /> Respetar campañas seleccionadas
          </label>
          <label className="inline-flex items-center gap-1.5 text-zinc-300 cursor-pointer pb-1.5">
            <input type="checkbox" checked={usarOrs} onChange={(e) => setUsarOrs(e.target.checked)} data-testid="usar-ors" /> Km reales (ORS) en los mejores
          </label>
          <Button size="sm" variant="outline" onClick={buscar} disabled={loading} data-testid="oportunidades-buscar" className="h-7 text-[11px] bg-transparent border-zinc-700 text-zinc-200 ml-auto"><RefreshCw className="w-3 h-3 mr-1" /> Recalcular</Button>
        </div>

        {data && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-400" data-testid="oportunidades-resumen">
            <span><b className="text-zinc-100">{data.n_destinos}</b> destinos · <b className="text-zinc-100">{data.n_trabajos}</b> trabajos sugeridos</span>
            <span>{data.n_lejanos} demasiado lejos (&gt; {data.umbrales.posible} km)</span>
            <span>{data.n_descartados_borrador} ya en el borrador</span>
            {hueco != null && <span>Hueco en {grupo.id}: <b className={hueco > 0 ? "text-emerald-300" : "text-rose-300"}>{hueco}</b> / {grupo.capacidad}</span>}
            <span className="w-full text-zinc-500" data-testid="oportunidades-metrica">ℹ️ {data.metrica_desc}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-[120px] pr-1">
          {loading && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline text-amber-400" /></div>}
          {!loading && data && destinos.length === 0 && <div className="text-center text-sm text-zinc-500 py-8" data-testid="oportunidades-vacio">Sin oportunidades dentro de los umbrales. Amplía el periodo, incluye atrasados/sin fecha o sube el umbral «Posible».</div>}
          {!loading && destinos.length > 0 && (
            <ul className="space-y-2" data-testid="oportunidades-lista">
              {destinos.map((d) => <DestinoItem key={d.destino_key} d={d} onAnadir={anadir} disabled={loading} />)}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
